<template>
    <div class="pc-page">

        <!-- PAGE HEADER -->
        <!-- <div class="pc-header">
            <div>
                <h1 class="pc-title">Points de contrôle</h1>
                <p class="pc-subtitle">
                    Sélectionnez une catégorie puis un type d'intervention pour gérer les points de contrôle.
                </p>
            </div>
        </div> -->

        <!-- LOADER -->
        <div v-if="loading" class="itc-loader-wrap">
            <div class="flex flex-col items-center justify-center py-24">
                <div class="flex gap-2 mb-4">
                    <div class="w-2 h-2 bg-orange-600 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                    <div class="w-2 h-2 bg-orange-600 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                    <div class="w-2 h-2 bg-orange-600 rounded-full animate-bounce"></div>
                </div>
                <p class="text-sm text-gray-500">Chargement des points de contrôle…</p>
            </div>
        </div>

        <!-- LAYOUT : sidebar-cat | sidebar-type | main -->
        <div v-else class="pc-layout">

            <!-- SIDEBAR 1 : CATÉGORIES -->
            <div class="pc-sidebar">
                <div class="pc-sidebar-header">Catégories</div>
                <div class="pc-folder-list">
                    <div
                        v-for="cat in categories"
                        :key="cat.key"
                        class="pc-folder-item"
                        :class="{ active: activeCategory === cat.key }"
                        @click="selectCategory(cat.key)">
                        <img v-if="cat.icon" :src="cat.icon" class="pc-folder-icon" alt="" />
                        <span class="pc-folder-label">{{ cat.label }}</span>
                        <span class="pc-folder-count">{{ countForCategory(cat.key) }}</span>
                    </div>
                </div>
            </div>

            <!-- SIDEBAR 2 : TYPE D'INTERVENTION -->
            <div class="pc-sidebar pc-sidebar--sub" :class="{ 'pc-sidebar--hidden': !activeCategory }">
                <div class="pc-sidebar-header">Type d'intervention</div>
                <div class="pc-folder-list">
                    <div v-if="allowedTypeIntervention.includes(t.value)" v-for="t in typesLogiques" :key="t.value" class="pc-folder-item pc-folder-item--sub" :class="{ active: activeTypeLogique === t.value }" @click="selectTypeLogique(t.value)">
                        <span class="pc-folder-label">{{ t.label }}</span>
                        <span class="pc-folder-count">{{ countForTypeLogique(t.value) }}</span>
                    </div>
                </div>
            </div>

            <!-- MAIN CONTENT -->
            <div class="pc-main">

                <!-- ÉTAT 1 : aucune catégorie -->
                <div v-if="!activeCategory" class="pc-no-select">
                    <div class="pc-no-select-icon"><i class="fas fa-folder-open"></i></div>
                    <p>Sélectionnez une catégorie<br />pour continuer</p>
                </div>

                <!-- ÉTAT 2 : catégorie ok, pas de type -->
                <div v-else-if="!activeTypeLogique" class="pc-no-select">
                    <div class="pc-no-select-icon"><i class="fas fa-list-ul"></i></div>
                    <p>Sélectionnez un type d'intervention<br />pour voir les points de contrôle</p>
                </div>

                <!-- ÉTAT 3 : tout sélectionné -->
                <template v-else>

                    <!-- TOPBAR -->
                    <div class="pc-topbar">
                        <div class="pc-breadcrumb">
                            <img v-if="activeCategoryObj && activeCategoryObj.icon" :src="activeCategoryObj.icon" class="pc-breadcrumb-icon" />
                            <span class="pc-breadcrumb-cat">{{ activeCategoryObj && activeCategoryObj.label }}</span>
                            <i class="fas fa-chevron-right pc-breadcrumb-sep"></i>
                            <span class="pc-breadcrumb-type">{{ activeTypeLogiqueObj && activeTypeLogiqueObj.label }}</span>
                        </div>
                        <div class="pc-toolbar-right">
                            <div class="pc-search-wrap">
                                <i class="fas fa-search pc-search-icon"></i>
                                <input
                                    v-model="filters.search"
                                    type="text"
                                    class="pc-search-input"
                                    placeholder="Rechercher..." />
                            </div>
                            <button class="pc-btn-primary" @click="openCreate">
                                <i class="fas fa-plus"></i>
                                Ajouter
                            </button>
                        </div>
                    </div>

                    <!-- LOADER -->
                    <div v-if="loading" class="pc-loader">
                        <div class="pc-spinner">
                            <i class="fas fa-circle-notch fa-spin"></i>
                        </div>
                        <span>Chargement...</span>
                    </div>

                    <!-- TABLE -->
                    <table v-else class="pc-table">
                        <thead>
                            <tr>
                                <th>Libellé</th>
                                <th>Section</th>
                                <th>Type de saisie</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="filteredItems.length === 0">
                                <td colspan="4">
                                    <div class="pc-empty">
                                        <div class="pc-empty-icon"><i class="fas fa-inbox"></i></div>
                                        <strong>{{ filters.search ? 'Aucun résultat' : 'Aucun point de contrôle' }}</strong>
                                        <p>{{ filters.search ? 'Modifiez votre recherche.' : 'Cliquez sur « Ajouter » pour commencer.' }}</p>
                                    </div>
                                </td>
                            </tr>
                            <tr
                                v-for="row in filteredItems"
                                :key="row.id"
                                class="pc-row">
                                <td class="pc-td-label">{{ row.label }}</td>
                                <td class="pc-td-section">
                                    <span v-if="row.section" class="pc-section-badge">{{ row.section }}</span>
                                    <span v-else class="pc-muted">—</span>
                                </td>
                                <td class="pc-td-type">
                                    <span class="pc-type-badge" :class="'pc-type-badge--' + (row.type_input || 'none')">
                                        <!-- <i :class="row.type_input === 'checkbox' ? 'fas fa-check-square' : row.type_input === 'number' ? 'fas fa-hashtag' : 'fas fa-minus'"></i> -->
                                        {{ row.type_input === 'checkbox' ? 'OUI / NON' : row.type_input === 'number' ? 'Nombre' : '—' }}
                                    </span>
                                </td>
                                <td class="pc-td-actions">
                                    <button class="pc-btn-icon pc-btn-edit" @click="openEdit(row)" title="Modifier">
                                        <i class="fas fa-pen"></i>
                                    </button>
                                    <button class="pc-btn-icon pc-btn-danger" @click="remove(row)" title="Supprimer">
                                        <i class="fas fa-trash"></i>
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                </template>
            </div>
        </div>

        <!-- ── MODALE ──────────────────────────────────── -->
        <transition name="pc-modal-fade">
            <div v-if="modal.open" class="pc-modal-overlay" @click.self="closeModal">
                <transition name="pc-modal-slide">
                    <div v-if="modal.open" class="pc-modal" role="dialog">

                        <!-- HEADER -->
                        <div class="pc-modal-header">
                            <div class="pc-modal-title-wrap">
                                <div class="pc-modal-icon">
                                    <i :class="modal.isEdit ? 'fas fa-pen' : 'fas fa-plus'"></i>
                                </div>
                                <div>
                                    <h2 class="pc-modal-title">
                                        {{ modal.isEdit ? 'Modifier le point de contrôle' : 'Nouveau point de contrôle' }}
                                    </h2>
                                    <p class="pc-modal-subtitle" v-if="activeCategoryObj && activeTypeLogiqueObj">
                                        {{ activeCategoryObj.label }} &rsaquo; {{ activeTypeLogiqueObj.label }}
                                    </p>
                                </div>
                            </div>
                            <button class="pc-modal-close" @click="closeModal">
                                <i class="fas fa-times"></i>
                            </button>
                        </div>

                        <!-- BODY -->
                        <div class="pc-modal-body">

                            <!-- Libellé -->
                            <div class="pc-field">
                                <label class="pc-label">
                                    Libellé <span class="pc-required">*</span>
                                </label>
                                <input
                                    v-model="form.label"
                                    class="pc-input"
                                    :class="{ 'pc-input--error': errors.label }"
                                    placeholder="Ex : Présence des EPI" />
                                <span v-if="errors.label" class="pc-field-error">{{ errors.label }}</span>
                            </div>

                            <!-- Section + Type -->
                            <div class="pc-field-row">
                                <div class="pc-field">
                                    <label class="pc-label">Section</label>
                                    <input
                                        v-model="form.section"
                                        class="pc-input"
                                        :placeholder="activeCategoryObj ? activeCategoryObj.label : 'Ex : Sécurité'" />
                                    <span class="pc-field-hint">Par défaut : nom de la catégorie</span>
                                </div>

                                <div class="pc-field">
                                    <label class="pc-label">
                                        Type de saisie <span class="pc-required">*</span>
                                    </label>
                                    <div class="pc-type-picker">
                                        <label class="pc-type-option" :class="{ active: form.type_input === 'checkbox' }">
                                            <input type="radio" v-model="form.type_input" value="checkbox" />
                                            <div class="pc-type-option-inner">
                                                <i class="fas fa-check-square pc-type-option-icon"></i>
                                                <span class="pc-type-option-label">Case à cocher</span>
                                                <span class="pc-type-option-desc">Oui / Non</span>
                                            </div>
                                        </label>
                                        <label class="pc-type-option" :class="{ active: form.type_input === 'number' }">
                                            <input type="radio" v-model="form.type_input" value="number" />
                                            <div class="pc-type-option-inner">
                                                <i class="fas fa-hashtag pc-type-option-icon"></i>
                                                <span class="pc-type-option-label">Nombre</span>
                                                <span class="pc-type-option-desc">Saisie libre</span>
                                            </div>
                                        </label>
                                    </div>
                                    <span v-if="errors.type_input" class="pc-field-error">{{ errors.type_input }}</span>
                                </div>
                            </div>

                        </div>

                        <!-- FOOTER -->
                        <div class="pc-modal-footer">
                            <button class="pc-btn-ghost" @click="closeModal">Annuler</button>
                            <button class="pc-btn-primary" :disabled="saving" @click="submit">
                                <i v-if="saving" class="fas fa-circle-notch fa-spin"></i>
                                <i v-else :class="modal.isEdit ? 'fas fa-save' : 'fas fa-plus'"></i>
                                {{ modal.isEdit ? 'Enregistrer' : 'Créer' }}
                            </button>
                        </div>

                    </div>
                </transition>
            </div>
        </transition>

    </div>
</template>

<script>
import TypeLogiqueIntervention from '@/config/TypeLogiqueIntervention'

const emptyForm = () => ({ label: '', section: '', type_input: '' })

// Normalise une chaîne : minuscules + suppression des accents
const normalize = str =>
    (str || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()

// Génère une clé snake_case depuis un libellé
const generateKey = label =>
    normalize(label)
        .replace(/[^a-z0-9\s_]/g, '')
        .replace(/\s+/g, '_')
        .replace(/_+/g, '_')

export default {
    name: 'PointControlesCrud',

    data() {
        return {
            items: [],
            categories: [],
            typesLogiques: TypeLogiqueIntervention,

            activeCategory: null,
            activeTypeLogique: null,

            loading: false,
            filters: { search: '' },

            modal: { open: false, isEdit: false },
            form: emptyForm(),
            editingId: null,
            errors: {},
            saving: false,

            allowedTypeIntervention: ['entretien', 'mise_en_service', 'installation'],

            loading: false,
        }
    },

    computed: {
        activeCategoryObj() {
            return this.categories.find(c => c.key === this.activeCategory) || null
        },
        activeTypeLogiqueObj() {
            return this.typesLogiques.find(t => t.value === this.activeTypeLogique) || null
        },

        filteredItems() {
            const s = normalize(this.filters.search)
            return this.items.filter(r => {
                if (r.categorie !== this.activeCategory) return false
                if (r.type_logique !== this.activeTypeLogique) return false
                if (s) {
                    const inLabel   = normalize(r.label).includes(s)
                    const inSection = normalize(r.section).includes(s)
                    if (!inLabel && !inSection) return false
                }
                return true
            })
        },
    },

    methods: {
        countForCategory(key) {
            return this.items.filter(r => r.categorie === key).length
        },
        countForTypeLogique(value) {
            return this.items.filter(r => r.categorie === this.activeCategory && r.type_logique === value).length
        },

        selectCategory(key) {
            if (this.activeCategory === key) return
            this.activeCategory = key
            this.activeTypeLogique = null
            this.filters.search = ''
        },
        selectTypeLogique(value) {
            this.activeTypeLogique = value
            this.filters.search = ''
        },

        async load() {
            this.loading = true
            try {
                const [res, cats] = await Promise.all([
                    this.$request.post('point-controle/list'),
                    this.$request.post('/reference/get-categories', {}),
                ])
                this.items      = Array.isArray(res) ? res : res.items || res.data || []
                this.categories = cats
            } finally {
                this.loading = false
            }
        },

        // ── Modale ──────────────────────────────────
        openCreate() {
            this.form    = emptyForm()
            this.errors  = {}
            this.editingId = null
            this.modal   = { open: true, isEdit: false }
        },

        openEdit(row) {
            this.form = {
                label:      row.label,
                section:    row.section || '',
                type_input: row.type_input || '',
            }
            this.errors    = {}
            this.editingId = row.id
            this.modal     = { open: true, isEdit: true }
        },

        closeModal() {
            this.modal.open = false
        },

        validate() {
            const e = {}
            if (!this.form.label.trim()) e.label      = 'Le libellé est obligatoire.'
            if (!this.form.type_input)   e.type_input = 'Choisissez un type de saisie.'
            this.errors = e
            return Object.keys(e).length === 0
        },

        resolvedSection() {
            // Si la section est vide, on utilise le label de la catégorie active
            return this.form.section.trim() || (this.activeCategoryObj ? this.activeCategoryObj.label : null)
        },

        async submit() {
            if (!this.validate()) return
            this.saving = true
            try {
                const payload = {
                    label:      this.form.label.trim(),
                    key:        generateKey(this.form.label),
                    section:    this.resolvedSection(),
                    type_input: this.form.type_input,
                }

                if (this.modal.isEdit) {
                    const res     = await this.$request.put(`point-controle/${this.editingId}`, payload)
                    const updated = res.item || res
                    const idx     = this.items.findIndex(i => i.id === this.editingId)
                    if (idx !== -1) this.$set(this.items, idx, { ...this.items[idx], ...updated })
                } else {
                    const res = await this.$request.post('point-controle/create', {
                        ...payload,
                        categorie:    this.activeCategory,
                        type_logique: this.activeTypeLogique,
                    })
                    this.items.unshift(res.item || res)
                }
                this.closeModal()
            } finally {
                this.saving = false
            }
        },

        async remove(row) {
            if (!confirm(`Supprimer « ${row.label} » ?`)) return
            await this.$request.delete(`point-controle/${row.id}`)
            this.items = this.items.filter(i => i.id !== row.id)
        },
    },

    mounted() {
        this.load()
    },
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,400&family=DM+Mono:wght@400;500&display=swap');

/* ── BASE ──────────────────────────────────────── */
.pc-page {
    font-family: 'DM Sans', sans-serif;
    padding: 32px;
    min-height: 100vh;
    background: #F5F4F1;
    color: #1A1916;
}

/* ── HEADER ────────────────────────────────────── */
.pc-header   { margin-bottom: 28px; }
.pc-title    { font-size: 22px; font-weight: 600; letter-spacing: -0.3px; color: #1A1916; margin: 0; }
.pc-subtitle { font-size: 13.5px; color: #6B6860; margin-top: 4px; line-height: 1.6; }

/* ── LAYOUT ────────────────────────────────────── */
.pc-layout {
    display: grid;
    grid-template-columns: 200px 180px 1fr;
    background: #fff;
    border: 1px solid #E4E2DC;
    border-radius: 12px;
    /* pas d'overflow:hidden ici pour éviter le scroll parasite */
    box-shadow: 0 1px 4px rgba(0,0,0,0.07);
    align-items: start;
}

/* ── SIDEBARS ──────────────────────────────────── */
.pc-sidebar       { border-right: 1px solid #E4E2DC; min-height: 520px; background: #FAFAF8; border-radius: 12px 0 0 12px; }
.pc-sidebar--sub  { background: #F7F6F3; border-radius: 0; transition: opacity 0.2s; }
.pc-sidebar--hidden { opacity: 0.4; pointer-events: none; }

.pc-sidebar-header {
    padding: 13px 14px;
    border-bottom: 1px solid #E4E2DC;
    font-size: 10.5px; font-weight: 600;
    letter-spacing: 0.8px; text-transform: uppercase; color: #A8A49D;
}
.pc-folder-list { padding: 6px; }
.pc-folder-item {
    display: flex; align-items: center; gap: 8px;
    padding: 8px 10px; border-radius: 6px; cursor: pointer;
    transition: background 0.15s, color 0.15s;
    font-size: 13px; font-weight: 450; color: #6B6860; user-select: none;
}
.pc-folder-item:hover  { background: #F0EFE9; color: #1A1916; }
.pc-folder-item.active { background: #1A1916; color: #fff; }
.pc-folder-item--sub.active { background: #3D3C39; color: #fff; }
.pc-folder-icon { width: 16px; height: 16px; object-fit: contain; flex-shrink: 0; opacity: 0.7; }
.pc-folder-item.active .pc-folder-icon { opacity: 1; filter: brightness(0) invert(1); }
.pc-folder-label { flex: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pc-folder-count {
    font-size: 10.5px; font-family: 'DM Mono', monospace;
    background: rgba(0,0,0,0.07); border-radius: 20px; padding: 1px 6px; font-weight: 500;
}
.pc-folder-item.active .pc-folder-count { background: rgba(255,255,255,0.2); }

/* ── MAIN ──────────────────────────────────────── */
.pc-main {
    display: flex;
    flex-direction: column;
    min-height: 520px;
    /* On laisse le contenu dicter sa hauteur, pas de overflow */
    border-radius: 0 12px 12px 0;
}

.pc-no-select {
    flex: 1; display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    padding: 60px 24px; text-align: center;
}
.pc-no-select-icon { font-size: 28px; color: #D4D1CB; margin-bottom: 12px; }
.pc-no-select p    { font-size: 13.5px; color: #B0ACA5; line-height: 1.6; }

/* ── TOPBAR ────────────────────────────────────── */
.pc-topbar {
    display: flex; align-items: center; justify-content: space-between;
    padding: 14px 18px; border-bottom: 1px solid #E4E2DC; gap: 12px;
}
.pc-breadcrumb { display: flex; align-items: center; gap: 7px; font-size: 12.5px; }
.pc-breadcrumb-icon { width: 14px; height: 14px; object-fit: contain; opacity: 0.45; }
.pc-breadcrumb-cat  { color: #6B6860; }
.pc-breadcrumb-sep  { font-size: 8px; color: #C8C4BC; }
.pc-breadcrumb-type { color: #1A1916; font-weight: 600; }
.pc-toolbar-right   { display: flex; align-items: center; gap: 10px; }

.pc-search-wrap { position: relative; }
.pc-search-icon {
    position: absolute; left: 10px; top: 50%;
    transform: translateY(-50%); color: #A8A49D; font-size: 11px; pointer-events: none;
}
.pc-search-input {
    background: #F5F4F1; border: 1px solid #E4E2DC; border-radius: 6px;
    padding: 7px 11px 7px 28px; font-family: 'DM Sans', sans-serif;
    font-size: 13px; color: #1A1916; outline: none; width: 200px;
    transition: border-color 0.15s, background 0.15s;
}
.pc-search-input:focus { border-color: #C8C4BC; background: #fff; }
.pc-search-input::placeholder { color: #C8C4BC; }

/* ── BUTTONS ───────────────────────────────────── */
.pc-btn-primary {
    display: inline-flex; align-items: center; gap: 6px;
    background: #1A1916; color: #fff; border: none; border-radius: 7px;
    padding: 7px 14px; font-family: 'DM Sans', sans-serif;
    font-size: 13px; font-weight: 500; cursor: pointer;
    transition: background 0.15s, transform 0.1s; white-space: nowrap;
}
.pc-btn-primary:hover   { background: #2D2C29; }
.pc-btn-primary:active  { transform: scale(0.98); }
.pc-btn-primary:disabled { background: #C8C4BC; cursor: not-allowed; }

.pc-btn-ghost {
    display: inline-flex; align-items: center; gap: 6px;
    background: transparent; color: #6B6860; border: 1px solid #E4E2DC;
    border-radius: 7px; padding: 7px 14px; font-family: 'DM Sans', sans-serif;
    font-size: 13px; font-weight: 500; cursor: pointer;
    transition: background 0.15s, color 0.15s;
}
.pc-btn-ghost:hover { background: #F5F4F1; color: #1A1916; }

.pc-btn-icon {
    background: none; border: none; cursor: pointer;
    padding: 5px 7px; border-radius: 5px; color: #A8A49D;
    font-size: 12px; transition: background 0.15s, color 0.15s; line-height: 1;
}
.pc-btn-edit:hover   { background: #EFF6FF; color: #3B82F6; }
.pc-btn-danger:hover { background: #FDF1F0; color: #D63B2F; }

/* ── LOADER ────────────────────────────────────── */
.pc-loader {
    display: flex; flex-direction: column; align-items: center;
    justify-content: center; gap: 12px;
    padding: 60px 24px; color: #A8A49D; font-size: 13px;
}
.pc-spinner { font-size: 24px; color: #C8C4BC; }

/* ── TABLE ─────────────────────────────────────── */
/* Pas de wrapper scrollable : la table s'étire naturellement */
.pc-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13.5px;
}
.pc-table thead tr { background: #FAFAF8; border-bottom: 2px solid #E4E2DC; }
.pc-table th {
    padding: 11px 16px; text-align: left;
    font-size: 10.5px; font-weight: 600;
    letter-spacing: 0.6px; text-transform: uppercase; color: #A8A49D;
    white-space: nowrap;
}
.pc-table td {
    padding: 13px 16px;
    border-bottom: 1px solid #F0EEE8;
    vertical-align: middle;
}
.pc-table tbody tr:last-child td { border-bottom: none; }
.pc-row { transition: background 0.1s; }
.pc-row:hover td { background: #FAFAF8; }

.pc-td-label   { font-weight: 500; color: #1A1916; width: 25%; }
.pc-td-section { width: 30%; }
.pc-td-type    { width: 20%; }
.pc-td-actions { text-align: right; white-space: nowrap; width: 5%; }
.pc-muted      { color: #C8C4BC; font-size: 13px; }

/* BADGES */
.pc-section-badge {
    font-size: 12px; background: #EFF6FF; color: #3B82F6;
    border-radius: 4px; padding: 2px 8px;
}
.pc-type-badge {
    display: inline-flex; align-items: center; gap: 5px;
    font-size: 12px; border-radius: 20px; padding: 3px 9px; font-weight: 500;
}
.pc-type-badge--checkbox { background: #F0FDF4; color: #16A34A; }
.pc-type-badge--number   { background: #FFF7ED; color: #D97706; }
.pc-type-badge--none     { background: #F5F4F1; color: #A8A49D; }

/* EMPTY */
.pc-empty       { padding: 52px 24px; text-align: center; }
.pc-empty-icon  { font-size: 30px; color: #C8C4BC; margin-bottom: 12px; }
.pc-empty strong { display: block; font-size: 14px; font-weight: 500; color: #6B6860; margin-bottom: 5px; }
.pc-empty p      { font-size: 13px; color: #A8A49D; }

/* ── MODAL ─────────────────────────────────────── */
.pc-modal-overlay {
    position: fixed; inset: 0;
    background: rgba(15, 14, 12, 0.5);
    backdrop-filter: blur(3px);
    z-index: 1000;
    display: flex; align-items: center; justify-content: center;
    padding: 24px;
}
.pc-modal {
    background: #fff; border-radius: 14px;
    width: 100%; max-width: 520px;
    box-shadow: 0 24px 60px rgba(0,0,0,0.2), 0 4px 16px rgba(0,0,0,0.1);
    overflow: hidden; display: flex; flex-direction: column;
}

.pc-modal-header {
    display: flex; align-items: flex-start; justify-content: space-between;
    padding: 22px 24px 18px; border-bottom: 1px solid #F0EEE8; gap: 12px;
}
.pc-modal-title-wrap { display: flex; align-items: flex-start; gap: 13px; }
.pc-modal-icon {
    width: 38px; height: 38px; border-radius: 9px;
    background: #1A1916; color: #fff;
    display: flex; align-items: center; justify-content: center;
    font-size: 14px; flex-shrink: 0;
}
.pc-modal-title    { font-size: 16px; font-weight: 600; color: #1A1916; margin: 0 0 2px; letter-spacing: -0.2px; }
.pc-modal-subtitle { font-size: 12px; color: #A8A49D; margin: 0; }
.pc-modal-close {
    background: none; border: none; cursor: pointer;
    color: #A8A49D; font-size: 14px; padding: 4px 6px;
    border-radius: 5px; transition: background 0.15s, color 0.15s; flex-shrink: 0;
}
.pc-modal-close:hover { background: #F5F4F1; color: #1A1916; }

.pc-modal-body {
    padding: 22px 24px;
    display: flex; flex-direction: column; gap: 18px;
}
.pc-field-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.pc-field     { display: flex; flex-direction: column; gap: 5px; }

.pc-label {
    font-size: 12.5px; font-weight: 600; color: #4B4A47;
    display: flex; align-items: center; gap: 5px;
}
.pc-required   { color: #D63B2F; }

.pc-input {
    background: #FAFAF8; border: 1px solid #E4E2DC; border-radius: 7px;
    padding: 9px 12px; font-family: 'DM Sans', sans-serif;
    font-size: 13.5px; color: #1A1916; outline: none;
    transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
    width: 100%; box-sizing: border-box;
}
.pc-input:focus {
    border-color: #1A1916; background: #fff;
    box-shadow: 0 0 0 3px rgba(26,25,22,0.07);
}
.pc-input::placeholder { color: #C8C4BC; }
.pc-input--error { border-color: #D63B2F; }
.pc-input--error:focus { box-shadow: 0 0 0 3px rgba(214,59,47,0.1); }

.pc-field-error { font-size: 11.5px; color: #D63B2F; }
.pc-field-hint  { font-size: 11.5px; color: #A8A49D; }

/* TYPE PICKER */
.pc-type-picker { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.pc-type-option input[type="radio"] { display: none; }
.pc-type-option-inner {
    display: flex; flex-direction: column; align-items: center;
    gap: 4px; padding: 12px 10px;
    border: 1.5px solid #E4E2DC; border-radius: 8px; background: #FAFAF8;
    transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
    text-align: center; cursor: pointer;
}
.pc-type-option-inner:hover { border-color: #C8C4BC; background: #fff; }
.pc-type-option.active .pc-type-option-inner {
    border-color: #1A1916; background: #1A1916;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}
.pc-type-option-icon  { font-size: 18px; color: #A8A49D; transition: color 0.15s; }
.pc-type-option.active .pc-type-option-icon { color: #fff; }
.pc-type-option-label { font-size: 12.5px; font-weight: 600; color: #1A1916; transition: color 0.15s; }
.pc-type-option.active .pc-type-option-label { color: #fff; }
.pc-type-option-desc  { font-size: 11px; color: #A8A49D; transition: color 0.15s; }
.pc-type-option.active .pc-type-option-desc { color: rgba(255,255,255,0.6); }

.pc-modal-footer {
    display: flex; align-items: center; justify-content: flex-end;
    gap: 10px; padding: 16px 24px;
    border-top: 1px solid #F0EEE8; background: #FAFAF8;
}

/* ── TRANSITIONS ───────────────────────────────── */
.pc-modal-fade-enter-active,
.pc-modal-fade-leave-active { transition: opacity 0.2s ease; }
.pc-modal-fade-enter,
.pc-modal-fade-leave-to     { opacity: 0; }

.pc-modal-slide-enter-active { transition: transform 0.22s cubic-bezier(0.34, 1.2, 0.64, 1), opacity 0.18s ease; }
.pc-modal-slide-leave-active { transition: transform 0.16s ease, opacity 0.16s ease; }
.pc-modal-slide-enter        { transform: translateY(16px) scale(0.98); opacity: 0; }
.pc-modal-slide-leave-to     { transform: translateY(8px) scale(0.99); opacity: 0; }
</style>